import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { UBER_VOUCHER_ENABLED } from '@rd/core/tokens/unleash-flags.token';
import { LoyaltyProgram, LoyaltyProgramCategory } from '@rd/types';
import { Observable } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import {
  ChangeLoyaltyProgramsFilterCategory,
  ChangeLoyaltyProgramsFilterKeyword,
  LoadLoyaltyPrograms,
} from '../store/actions/loyalty-program.actions';
import {
  getAirlineLoyaltyPrograms,
  getCashBackLoyaltyPrograms,
  getCashBackToAccountLoyaltyProgram,
  getCashBackToCardLoyaltyProgram,
  getCashBackToPlatformLoyaltyProgram,
  getFilteredPointsTransferLoyaltyPrograms,
  getHotelLoyaltyPrograms,
  getLoyaltyProgramIndexError,
  getLoyaltyProgramIndexLoading,
  getLoyaltyProgramIndexSuccess,
  getLoyaltyPrograms,
  getRetailLoyaltyPrograms,
} from '../store/selectors/loyalty-program.selectors';

@Injectable({
  providedIn: 'root',
})
export class LoyaltyProgramFacade {
  private isUberVoucherEnabled = inject(UBER_VOUCHER_ENABLED);

  constructor(private store: Store) {}

  cashBackToCardLoyaltyProgram$: Observable<LoyaltyProgram | null> =
    this.store.select(getCashBackToCardLoyaltyProgram);

  cashBackToAccountLoyaltyProgram$: Observable<LoyaltyProgram | null> =
    this.store.select(getCashBackToAccountLoyaltyProgram);

  cashBackToPlatformLoyaltyProgram$: Observable<LoyaltyProgram | null> =
    this.store.select(getCashBackToPlatformLoyaltyProgram);

  loadLoyaltyProgramsLoading$: Observable<boolean> = this.store.select(
    getLoyaltyProgramIndexLoading,
  );

  loadLoyaltyProgramsError$: Observable<boolean> = this.store.select(
    getLoyaltyProgramIndexError,
  );

  loadLoyaltyProgramsSuccess$: Observable<boolean> = this.store.select(
    getLoyaltyProgramIndexSuccess,
  );

  loyaltyProgramsMap$: Observable<{ [id: string]: LoyaltyProgram }> =
    this.loadLoyaltyProgramsLoading$.pipe(
      filter((loading) => loading === false),
      switchMap(() => this.store.select(getLoyaltyPrograms)),
    );

  loyaltyPrograms$: Observable<LoyaltyProgram[]> = this.store
    .select(getLoyaltyPrograms)
    .pipe(
      map((loyaltyProgramMap) => {
        const loyaltyPrograms = Object.values(loyaltyProgramMap);
        loyaltyPrograms.sort((a, b) => a.sortIndex - b.sortIndex);
        return loyaltyPrograms;
      }),
    );

  cashBackLoyaltyPrograms$: Observable<LoyaltyProgram[]> = this.store.select(
    getCashBackLoyaltyPrograms,
  );

  airlineLoyaltyPrograms$: Observable<LoyaltyProgram[]> = this.store.select(
    getAirlineLoyaltyPrograms,
  );

  hotelLoyaltyPrograms$: Observable<LoyaltyProgram[]> = this.store.select(
    getHotelLoyaltyPrograms,
  );

  retailLoyaltyPrograms$: Observable<LoyaltyProgram[]> = this.store.select(
    getRetailLoyaltyPrograms,
  );

  filteredPointsTransferLoyaltyPrograms$: Observable<LoyaltyProgram[]> =
    this.store.select(
      getFilteredPointsTransferLoyaltyPrograms(this.isUberVoucherEnabled),
    );

  getLoyaltyProgram(id: string): Observable<LoyaltyProgram | null> {
    return this.store
      .select(getLoyaltyPrograms)
      .pipe(map((loyaltyProgramMap) => loyaltyProgramMap[id]));
  }

  loadLoyaltyPrograms(): void {
    this.store.dispatch(new LoadLoyaltyPrograms());
  }

  changeLoyaltyProgramsFilterCategory(
    category: LoyaltyProgramCategory | 'all',
  ): void {
    this.store.dispatch(new ChangeLoyaltyProgramsFilterCategory(category));
  }

  changeLoyaltyProgramsFilterKeyword(keyword: string): void {
    this.store.dispatch(new ChangeLoyaltyProgramsFilterKeyword(keyword));
  }
}
